import React, { useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';
import { db } from '../firebase/config';
import { EventProps } from './EventCard';

const CMS_PASSWORD = 'Paraclimbing';
const EVENTS_COLLECTION = 'cms_events';
const SPONSORS_COLLECTION = 'cms_bk_sponsors';

const EVENT_TYPES = ['international', 'competitions', 'workshops', 'social'];

// Format a single ISO date to a localised display string
const formatDate = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Build the display date string from start + optional end
const buildDisplayDate = (startDate: string, endDate: string): string => {
  if (!startDate) return '';
  if (!endDate || endDate === startDate) return formatDate(startDate);
  const s = new Date(startDate + 'T00:00:00');
  const e = new Date(endDate + 'T00:00:00');
  // Same month → "20–21 juni 2026"
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()}–${e.getDate()} ${s.toLocaleDateString('nl-BE', { month: 'long' })} ${s.getFullYear()}`;
  }
  return `${formatDate(startDate)} – ${formatDate(endDate)}`;
};

interface FormState {
  title: string;
  startDate: string;
  endDate: string;
  location: string;
  description: string;
  imageUrl: string;
  type: string;
  externalUrl: string;
}

const emptyForm = (): FormState => ({
  title: '',
  startDate: '',
  endDate: '',
  location: '',
  description: '',
  imageUrl: '/assets/podium.png',
  type: 'international',
  externalUrl: '',
});

interface SponsorData {
  id: string;
  name: string;
  logoUrl: string;
  websiteUrl: string;
  order: number;
}

interface SponsorFormState {
  name: string;
  logoUrl: string;
  websiteUrl: string;
  order: string;
}

const emptySponsorForm = (): SponsorFormState => ({
  name: '',
  logoUrl: '',
  websiteUrl: '',
  order: '0',
});

interface CMSModalProps {
  onClose: () => void;
}

const CMSModal: React.FC<CMSModalProps> = ({ onClose }) => {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [activeTab, setActiveTab] = useState<'events' | 'sponsors'>('events');

  const [events, setEvents] = useState<EventProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const [sponsors, setSponsors] = useState<SponsorData[]>([]);
  const [editingSponsorId, setEditingSponsorId] = useState<string | null>(null);
  const [sponsorForm, setSponsorForm] = useState<SponsorFormState>(emptySponsorForm());
  const [savingSponsor, setSavingSponsor] = useState(false);
  const [deleteSponsorConfirm, setDeleteSponsorConfirm] = useState<string | null>(null);
  const [downloadingLogos, setDownloadingLogos] = useState(false);

  // Load events from Firestore once authenticated
  useEffect(() => {
    if (!authed) return;
    setLoading(true);
    const unsub = db.collection(EVENTS_COLLECTION)
      .orderBy('createdAt', 'desc')
      .onSnapshot(snap => {
        setEvents(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as EventProps)));
        setLoading(false);
      }, () => setLoading(false));
    return unsub;
  }, [authed]);

  // Load sponsors from Firestore once authenticated
  useEffect(() => {
    if (!authed) return;
    const unsub = db.collection(SPONSORS_COLLECTION)
      .orderBy('order', 'asc')
      .onSnapshot(snap => {
        setSponsors(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as SponsorData)));
      }, () => {});
    return unsub;
  }, [authed]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === CMS_PASSWORD) {
      setAuthed(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password.');
    }
  };

  const handleFormChange = (field: keyof FormState, value: string) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const startAdd = () => {
    setEditingId('__new__');
    setForm(emptyForm());
  };

  const startEdit = (ev: EventProps) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title,
      startDate: ev.startDate || '',
      endDate: ev.endDate || '',
      location: ev.location,
      description: ev.description,
      imageUrl: ev.imageUrl,
      type: ev.type || 'international',
      externalUrl: ev.externalUrl || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleSponsorFormChange = (field: keyof SponsorFormState, value: string) => {
    setSponsorForm(f => ({ ...f, [field]: value }));
  };

  const startAddSponsor = () => {
    setEditingSponsorId('__new__');
    setSponsorForm(emptySponsorForm());
  };

  const startEditSponsor = (sp: SponsorData) => {
    setEditingSponsorId(sp.id);
    setSponsorForm({
      name: sp.name,
      logoUrl: sp.logoUrl,
      websiteUrl: sp.websiteUrl,
      order: String(sp.order ?? 0),
    });
  };

  const cancelEditSponsor = () => {
    setEditingSponsorId(null);
    setSponsorForm(emptySponsorForm());
  };

  const handleSaveSponsor = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSponsor(true);
    try {
      const websiteUrl = sponsorForm.websiteUrl.match(/^https?:\/\//)
        ? sponsorForm.websiteUrl
        : `https://${sponsorForm.websiteUrl}`;
      const data = {
        name: sponsorForm.name,
        logoUrl: sponsorForm.logoUrl,
        websiteUrl,
        order: parseInt(sponsorForm.order, 10) || 0,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      };
      if (editingSponsorId === '__new__') {
        await db.collection(SPONSORS_COLLECTION).add({
          ...data,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      } else if (editingSponsorId) {
        await db.collection(SPONSORS_COLLECTION).doc(editingSponsorId).update(data);
      }
      cancelEditSponsor();
    } finally {
      setSavingSponsor(false);
    }
  };

  const handleDeleteSponsor = async (id: string) => {
    await db.collection(SPONSORS_COLLECTION).doc(id).delete();
    setDeleteSponsorConfirm(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const displayDate = buildDisplayDate(form.startDate, form.endDate || form.startDate);
      const data = {
        title: form.title,
        startDate: form.startDate,
        endDate: form.endDate || form.startDate,
        date: displayDate,
        location: form.location,
        description: form.description,
        imageUrl: form.imageUrl,
        type: form.type,
        externalUrl: form.externalUrl
          ? form.externalUrl.match(/^https?:\/\//) ? form.externalUrl : `https://${form.externalUrl}`
          : null,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      };
      if (editingId === '__new__') {
        await db.collection(EVENTS_COLLECTION).add({
          ...data,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      } else if (editingId) {
        await db.collection(EVENTS_COLLECTION).doc(editingId).update(data);
      }
      cancelEdit();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    await db.collection(EVENTS_COLLECTION).doc(id).delete();
    setDeleteConfirm(null);
  };

  const handleDownloadLogos = async () => {
    if (!('showDirectoryPicker' in window)) {
      alert('Your browser does not support folder selection. Please use Chrome or Edge.');
      return;
    }
    setDownloadingLogos(true);
    let failed = 0;
    try {
      const dirHandle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
      for (const sp of sponsors) {
        try {
          const res = await fetch(sp.logoUrl);
          const blob = await res.blob();
          const urlPath = sp.logoUrl.split('?')[0];
          const urlFilename = urlPath.split('/').pop() || '';
          const ext = urlFilename.includes('.')
            ? urlFilename.split('.').pop()
            : (blob.type.split('/')[1] || 'png');
          const safeName = sp.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
          const filename = `${safeName}.${ext}`;
          const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
        } catch {
          failed++;
          console.warn(`Could not download logo for ${sp.name} (possibly blocked by CORS)`);
        }
      }
      const ok = sponsors.length - failed;
      alert(`Done! ${ok} logo(s) saved.${failed > 0 ? ` ${failed} failed (likely blocked by CORS).` : ''}`);
    } catch (err: any) {
      if (err?.name !== 'AbortError') alert('Error: ' + err?.message);
    } finally {
      setDownloadingLogos(false);
    }
  };

  // Backdrop click closes
  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-red-600 text-white rounded-t-2xl">
          <h2 className="text-xl font-bold">Activities CMS</h2>
          <button onClick={onClose} className="text-white hover:text-red-200 text-2xl leading-none">&times;</button>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          {/* Password gate */}
          {!authed ? (
            <form onSubmit={handleLogin} className="max-w-sm mx-auto mt-8 space-y-4">
              <p className="text-gray-600 text-center">Enter the CMS password to continue.</p>
              <input
                type="password"
                autoFocus
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-red-500"
              />
              {passwordError && <p className="text-red-600 text-sm">{passwordError}</p>}
              <button
                type="submit"
                className="w-full bg-red-600 text-white font-bold py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Login
              </button>
            </form>
          ) : (
            <>
              {/* Tabs */}
              <div className="flex gap-2 mb-6 border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('events')}
                  className={`pb-2 px-4 font-semibold text-sm transition-colors ${activeTab === 'events' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Events
                </button>
                <button
                  onClick={() => setActiveTab('sponsors')}
                  className={`pb-2 px-4 font-semibold text-sm transition-colors ${activeTab === 'sponsors' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  BK2026 Sponsors
                </button>
              </div>

              {activeTab === 'events' && <>
              {/* Add / Edit form */}
              {editingId ? (
                <form onSubmit={handleSave} className="space-y-4 mb-8 bg-gray-50 rounded-xl p-5 border">
                  <h3 className="font-bold text-lg text-dark">{editingId === '__new__' ? 'Add event' : 'Edit event'}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                      <input required value={form.title} onChange={e => handleFormChange('title', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select value={form.type} onChange={e => handleFormChange('type', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500">
                        {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start date *</label>
                      <input required type="date" value={form.startDate} onChange={e => handleFormChange('startDate', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End date (leave blank if single day)</label>
                      <input type="date" value={form.endDate} onChange={e => handleFormChange('endDate', e.target.value)}
                        min={form.startDate}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                      <input required value={form.location} onChange={e => handleFormChange('location', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                      <textarea required rows={3} value={form.description} onChange={e => handleFormChange('description', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                      <input value={form.imageUrl} onChange={e => handleFormChange('imageUrl', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">External URL (optional)</label>
                      <input value={form.externalUrl || ''} onChange={e => handleFormChange('externalUrl', e.target.value)}
                        placeholder="https://..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button type="submit" disabled={saving}
                      className="bg-red-600 text-white font-bold px-6 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button type="button" onClick={cancelEdit}
                      className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-100 transition-colors">
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <button onClick={startAdd}
                  className="mb-6 bg-red-600 text-white font-bold px-5 py-2 rounded-lg hover:bg-red-700 transition-colors">
                  + Add event
                </button>
              )}

              {/* Event list */}
              {loading ? (
                <p className="text-gray-500">Loading…</p>
              ) : events.length === 0 ? (
                <p className="text-gray-500">No events yet. Add one above.</p>
              ) : (
                <div className="space-y-3">
                  {events.map(ev => (
                    <div key={ev.id} className="flex items-start justify-between gap-4 border rounded-xl px-4 py-3 bg-white shadow-sm">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-dark truncate">{ev.title}</p>
                        <p className="text-sm text-gray-500">{ev.date} · {ev.location}</p>
                        <span className="inline-block text-xs bg-gray-100 text-gray-600 rounded px-2 py-0.5 mt-1">{ev.type}</span>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => startEdit(ev)}
                          className="text-sm border border-gray-300 px-3 py-1 rounded-lg hover:bg-gray-50 transition-colors">
                          Edit
                        </button>
                        {deleteConfirm === ev.id ? (
                          <>
                            <button onClick={() => handleDelete(ev.id)}
                              className="text-sm bg-red-600 text-white px-3 py-1 rounded-lg hover:bg-red-700 transition-colors">
                              Confirm
                            </button>
                            <button onClick={() => setDeleteConfirm(null)}
                              className="text-sm border border-gray-300 px-3 py-1 rounded-lg hover:bg-gray-50 transition-colors">
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button onClick={() => setDeleteConfirm(ev.id)}
                            className="text-sm border border-red-300 text-red-600 px-3 py-1 rounded-lg hover:bg-red-50 transition-colors">
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              </>}

              {activeTab === 'sponsors' && <>
                {/* Sponsor Add / Edit form */}
                {editingSponsorId ? (
                  <form onSubmit={handleSaveSponsor} className="space-y-4 mb-8 bg-gray-50 rounded-xl p-5 border">
                    <h3 className="font-bold text-lg text-dark">{editingSponsorId === '__new__' ? 'Add sponsor' : 'Edit sponsor'}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                        <input required value={sponsorForm.name} onChange={e => handleSponsorFormChange('name', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Display order</label>
                        <input type="number" value={sponsorForm.order} onChange={e => handleSponsorFormChange('order', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL *</label>
                        <input required value={sponsorForm.logoUrl} onChange={e => handleSponsorFormChange('logoUrl', e.target.value)}
                          placeholder="https://..."
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Website URL *</label>
                        <input required value={sponsorForm.websiteUrl} onChange={e => handleSponsorFormChange('websiteUrl', e.target.value)}
                          placeholder="https://..."
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button type="submit" disabled={savingSponsor}
                        className="bg-red-600 text-white font-bold px-6 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
                        {savingSponsor ? 'Saving…' : 'Save'}
                      </button>
                      <button type="button" onClick={cancelEditSponsor}
                        className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-100 transition-colors">
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="flex flex-wrap gap-3 mb-6">
                    <button onClick={startAddSponsor}
                      className="bg-red-600 text-white font-bold px-5 py-2 rounded-lg hover:bg-red-700 transition-colors">
                      + Add sponsor
                    </button>
                    <button
                      onClick={handleDownloadLogos}
                      disabled={downloadingLogos || sponsors.length === 0}
                      className="border border-gray-300 text-gray-700 font-semibold px-5 py-2 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      {downloadingLogos ? 'Downloading…' : '⬇ Download all logos'}
                    </button>
                  </div>
                )}

                {/* Sponsor list */}
                {sponsors.length === 0 ? (
                  <p className="text-gray-500">No sponsors yet. Add one above.</p>
                ) : (
                  <div className="space-y-3">
                    {sponsors.map(sp => (
                      <div key={sp.id} className="flex items-center justify-between gap-4 border rounded-xl px-4 py-3 bg-white shadow-sm">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <img src={sp.logoUrl} alt={sp.name} className="h-10 w-20 object-contain shrink-0" />
                          <div className="min-w-0">
                            <p className="font-semibold text-dark truncate">{sp.name}</p>
                            <p className="text-sm text-gray-500 truncate">{sp.websiteUrl}</p>
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button onClick={() => startEditSponsor(sp)}
                            className="text-sm border border-gray-300 px-3 py-1 rounded-lg hover:bg-gray-50 transition-colors">
                            Edit
                          </button>
                          {deleteSponsorConfirm === sp.id ? (
                            <>
                              <button onClick={() => handleDeleteSponsor(sp.id)}
                                className="text-sm bg-red-600 text-white px-3 py-1 rounded-lg hover:bg-red-700 transition-colors">
                                Confirm
                              </button>
                              <button onClick={() => setDeleteSponsorConfirm(null)}
                                className="text-sm border border-gray-300 px-3 py-1 rounded-lg hover:bg-gray-50 transition-colors">
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button onClick={() => setDeleteSponsorConfirm(sp.id)}
                              className="text-sm border border-red-300 text-red-600 px-3 py-1 rounded-lg hover:bg-red-50 transition-colors">
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CMSModal;
